import { useCallback, useEffect, useRef } from 'react'

import { useDesktopContextMenuState } from '../components/DesktopGrid/DesktopContextMenuContext'
import { isPortalOriginEvent } from './portalEvents'
import { CONTEXT_MENU_DELAY_MS } from './touchDrag.constants'
import { useTouchEnvironment } from './useTouchEnvironment'
import { useTouchMovement } from './useTouchMovement'

type UseTouchContextMenuOptions = {
  enabled?: boolean
}

export const useTouchContextMenu = ({
  enabled = true
}: UseTouchContextMenuOptions = {}) => {
  const isTouchEnvironment = useTouchEnvironment()
  const { isContextMenuOpen, setIsContextMenuOpen, closeContextMenu } =
    useDesktopContextMenuState()
  const touchHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearContextMenuTimer = useCallback(() => {
    if (touchHoldTimerRef.current) {
      clearTimeout(touchHoldTimerRef.current)
      touchHoldTimerRef.current = null
    }
  }, [])

  const { handleTouchStart: baseHandleTouchStart, ...touchMovementHandlers } =
    useTouchMovement({
      onMovementExceedsTolerance: () => {
        clearContextMenuTimer()
        closeContextMenu()
      },
      enabled: isTouchEnvironment && enabled
    })

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // A touch inside content the item portalled out of itself (a widget's
      // modal) still traverses this handler, and a long press there must not
      // open the item's own menu — or start tracking the press as a drag.
      if (isPortalOriginEvent(e)) return
      baseHandleTouchStart(e)
      if (!isTouchEnvironment || !enabled) return

      touchHoldTimerRef.current = setTimeout(() => {
        setIsContextMenuOpen(true)
        touchHoldTimerRef.current = null
      }, CONTEXT_MENU_DELAY_MS)
    },
    [baseHandleTouchStart, isTouchEnvironment, enabled, setIsContextMenuOpen]
  )

  const handleTouchEnd = useCallback(() => {
    touchMovementHandlers.handleTouchEnd()
    clearContextMenuTimer()
  }, [touchMovementHandlers, clearContextMenuTimer])

  const handleTouchCancel = useCallback(() => {
    touchMovementHandlers.handleTouchCancel()
    clearContextMenuTimer()
    closeContextMenu()
  }, [touchMovementHandlers, clearContextMenuTimer, closeContextMenu])

  useEffect(() => {
    return () => {
      if (touchHoldTimerRef.current) {
        clearTimeout(touchHoldTimerRef.current)
      }
    }
  }, [])

  return {
    isContextMenuOpen,
    setIsContextMenuOpen,
    closeContextMenu,
    handleTouchStart,
    handleTouchMove: touchMovementHandlers.handleTouchMove,
    handleTouchEnd,
    handleTouchCancel
  }
}
